import { ApolloError } from '@apollo/client';
import Big from 'big.js';
import { cloneDeep, omit } from 'lodash';
import { toast } from 'react-toastify';

import { Address } from '../gql/addressGql';
import { Car } from '../gql/carGql';
import { Customer } from '../gql/customerGql';
import { Deal, DealStateEnum, dealPodAndUserIdKeys } from '../gql/dealGql';
import {
  DOC_FEE,
  ProcessorEnum,
  TITLE_FEE,
  stripDeprecatedFinancialInfoFields,
} from '../gql/financialInfoGql';
import {
  DealSource,
  DealType,
  FollowUp,
  FollowUpStatusEnum,
  FollowUpTypeEnum,
  CreditScore as GeneratedCreditScore,
  Deal as GeneratedDeal,
  GetFeesQueryVariables,
  Maybe,
  PaymentEstimateInput,
  Payoff,
  ProductSlug,
  StateAbbreviation,
  TemporaryData,
  TtFuelType,
  TtGetFeesSourceType,
  UpdateCreditAppOnDealMutationResult,
} from '../gql/generated/graphql';

import { CreditAppOnDeal } from '../components/CreditApplication/useCreditAppInitialValues';
import { PayoffCheckInformationFormValues } from '../components/PayoffInfo/PayoffCheckInformationModal';
import { StructuringFollowUpDeals } from '../components/StructuringFollowUp/StructuringFollowUpTable';
import { LE_TIME_ZONE } from '../constants/LeaseEndInfo';
import { PermissionEnum } from '../constants/permissions';

import { transformVehicleType } from './cars';
import { getIsSecureLinkActive } from './customers';

export const isDeal = (
  obj: Deal | TemporaryData | Payoff | PayoffCheckInformationFormValues,
): obj is Deal => (obj as Deal)?.car?.payoff !== undefined;

export const getDealAgeColor = (age: number) => {
  if (age >= 0 && age <= 30) {
    return 'green';
  }

  if (age >= 31 && age <= 45) {
    return 'primary';
  }

  if (age >= 46 && age <= 75) {
    return 'americanOrange';
  }

  return 'errorsRed';
};

export const getFollowUpColor = (followUp: FollowUp) => {
  if (!followUp.status) {
    return '';
  }

  const colorMap: Record<FollowUpStatusEnum, string> = {
    [FollowUpStatusEnum.Scheduled]: 'primary',
    [FollowUpStatusEnum.Due]:
      followUp.type === FollowUpTypeEnum.Appointment ? 'americanOrange' : 'royalPurple',
    [FollowUpStatusEnum.Completed]: '',
    [FollowUpStatusEnum.Deleted]: '',
  };

  return colorMap[followUp.status];
};

export const getDealAge = (date?: string, timezone?: string) => {
  if (!date) {
    return 0;
  }

  const timeZone = timezone || LE_TIME_ZONE;

  const today = new Date(new Date().toLocaleString('en-US', { timeZone }));

  const diffTime = Math.abs(today.getTime() - new Date(date).getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Number(diffDays);
};

export const handleClaimDealError = (e: unknown) => {
  if (e instanceof ApolloError) {
    toast.error(e.message);
    return;
  }

  toast.error('Failed to assign deal.  Please refresh the page');
};

export type ProcessorOption = {
  label: ProcessorEnum;
  value: ProcessorEnum;
};

export const processorOptions: ProcessorOption[] = Object.values(ProcessorEnum).map((value) => ({
  label: value,
  value,
}));

// Address country is server-owned and not part of addressInput. Postal codes are stored
// upper-case (A1A 1A1); US zips are digits so this is a no-op for them.
const cleanAddressForUpsert = <T extends Address | null | undefined>(address: T): T =>
  address
    ? ({
        ...omit(address, 'country'),
        zip: address.zip ? address.zip.toUpperCase() : address.zip,
      } as T)
    : address;

export const cleanCustomerForUpsert = <T extends Customer | null | undefined>(customer: T): T => {
  const cleaned = cloneDeep(customer);
  if (!cleaned) {
    return cleaned;
  }
  cleaned.address = cleanAddressForUpsert(cleaned.address);
  cleaned.prev_address = cleanAddressForUpsert(cleaned.prev_address);
  cleaned.mailing_address = cleanAddressForUpsert(cleaned.mailing_address);
  return cleaned;
};

export const cleanDealForUpdate = (deal: Deal) => {
  const newDeal = cloneDeep(deal);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  delete newDeal.customer.ssn_last_4;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  delete newDeal.customer.ssn_last_6;

  delete newDeal.fs_pod;
  delete newDeal.consents;
  delete newDeal.tags;
  delete newDeal.setter;
  delete newDeal.closer;
  delete newDeal.closer2;
  delete newDeal.structuring_manager;
  delete newDeal.funding_clerk;
  delete newDeal.title_clerk;
  delete newDeal.title_clerk2;
  delete newDeal.customer.auth0_id;
  delete newDeal.cobuyer?.auth0_id;
  delete newDeal.contact;
  delete newDeal.second_contact;
  delete newDeal.notifications;

  // These are local-only Formik fields derived from deal_products, not part of dealInput
  delete newDeal.tire_wheel_price;
  delete newDeal.tire_wheel_cost;
  delete newDeal.key_replacement_price;
  delete newDeal.key_replacement_cost;
  delete newDeal.deal_products;

  // has_funding_jacket is read-only, derived server-side from deal_jackets (LE-18690);
  // it is not part of dealInput, so strip it from every mutation payload.
  delete newDeal.has_funding_jacket;

  // Country is fixed at creation and the mileage unit follows it; neither is part of dealInput.
  delete newDeal.country;
  delete newDeal.car?.mileage_unit;
  newDeal.customer = cleanCustomerForUpsert(newDeal.customer);
  newDeal.cobuyer = cleanCustomerForUpsert(newDeal.cobuyer);

  // PEN/product fields are owned by deal_products and no longer exist on the
  // financialInfoInput type — strip them from every mutation payload.
  if (newDeal.financial_info != null) {
    newDeal.financial_info = stripDeprecatedFinancialInfoFields(newDeal.financial_info);
  }

  // Avoids sending null values that update assignments
  // and allow users to claim the deal again
  dealPodAndUserIdKeys.forEach((property) => {
    if (!newDeal[property]) {
      delete newDeal[property];
    }
  });

  if (newDeal.deal_dates != null) {
    const { dates, custom_dates } = newDeal.deal_dates;
    // Strip id and deal_id from deal_dates to match gql input type.
    // Required because of a refactor for 2.0.
    newDeal.deal_dates = { dates, custom_dates };
  }

  return newDeal;
};

export const cleanDealForEstimateUpsert = (deal: Deal) => {
  const cleanDeal = cleanDealForUpdate(deal);
  delete cleanDeal.customer.has_last_4_ssn;
  delete cleanDeal.customer.has_last_6_ssn;
  delete cleanDeal.follow_up;
  return cleanDeal;
};

export const parseTempInfoAsDeal = (tempData: TemporaryData) => {
  const deal = new Deal();
  deal.car = new Car();
  deal.customer = new Customer();

  if (!tempData) {
    return deal;
  }

  const {
    year,
    make,
    model,
    vin,
    mileage,
    lh_account_number: account_number,
    lienholder: lienholder_name,
    first_name,
    last_name,
    ssn_id,
    dob,
    phone_number,
    zip,
  } = tempData;

  Object.assign(deal, {
    car: {
      year,
      make,
      model,
      vin,
      mileage,
      payoff: { lienholder_name, account_number },
    },
    customer: {
      first_name,
      last_name,
      ssn_id,
      dob,
      phone_number,
      address: { zip },
    },
  });

  return deal;
};

type MapDealToGetTTFeesVariablesOptions =
  | {
      ttEndpoint: 'createTransaction';
    }
  | {
      ttEndpoint: 'getFees';
      ttGetFeesSource: TtGetFeesSourceType;
    };

export const mapDealToGetTTFeesVariables = (
  deal: Deal,
  options: MapDealToGetTTFeesVariablesOptions,
): GetFeesQueryVariables => {
  const { ttEndpoint } = options;
  const ttGetFeesSource = ttEndpoint === 'getFees' ? options.ttGetFeesSource : undefined;

  const { customer, car, financial_info, deal_products } = deal;
  // Warranty (VSC) price for the tax calc comes from deal_products now; the
  // legacy financial_info.vsc_price is only a fallback (no longer fetched).
  const vscDealProductPrice = deal_products?.find(
    (dealProduct) => dealProduct?.product_catalog?.slug === ProductSlug.Vsc,
  )?.price;

  return {
    ...(ttEndpoint === 'getFees' ? { uniqueId: String(deal.id) } : {}),
    state: customer.address.state as StateAbbreviation,
    previouslyTitledState: (car.license_plate_state || customer.address.state) as StateAbbreviation,
    city: customer.address.city ?? '',
    county: customer.address.county ?? '',
    zip: customer.address.zip,
    vin: car.vin,
    make: car.make,
    model: car.model,
    year: parseInt(car.year, 10),
    vehicleType: transformVehicleType(car.vehicle_type ?? ''),
    fuelType:
      car.fuel_type && TtFuelType[car.fuel_type as TtFuelType]
        ? (car.fuel_type as TtFuelType)
        : TtFuelType.Gasoline,
    firstName: customer.first_name,
    lastName: customer.last_name,
    retailBookValue: car.retail_book_value ?? 0,
    bookValue: car.book_value ?? 0,
    docFee: financial_info?.doc_fee ?? DOC_FEE,
    payoff: car.payoff.vehicle_payoff ?? 0,
    estimatedPayoff: car.payoff.estimated_payoff ?? 0,
    ssn: '111-11-1111',
    warranty: vscDealProductPrice ?? financial_info?.vsc_price ?? 0,
    ...(ttEndpoint === 'getFees' ? { source: ttGetFeesSource } : {}),
  };
};

export const mapDealToGetPaymentEstimateVariables = (
  deal: GeneratedDeal,
  ttGetFeesSource: TtGetFeesSourceType,
): { data: PaymentEstimateInput } => ({
  data: {
    payoff: deal?.car?.payoff?.vehicle_payoff ?? 0,
    creditScore: GeneratedCreditScore.Good,
    term: Number(deal.financial_info?.term),
    moneyDown: deal.financial_info?.money_down ?? 0,
    zipCode: deal?.customer?.address?.zip as string,
    feesInputFromDD: {
      ...mapDealToGetTTFeesVariables(deal as Deal, {
        ttEndpoint: 'getFees',
        ttGetFeesSource,
      }),
      docFee: DOC_FEE + TITLE_FEE, // title is included in doc fee for payment estimation purposes
    },
    financialInfoId: deal?.financial_info?.id,
  },
});

export const getIsClickable = (
  deal: Maybe<Deal | GeneratedDeal | StructuringFollowUpDeals[number]>,
  permissions: Set<PermissionEnum>,
) => {
  if (!deal || deal.state === DealStateEnum.StructuringInProgress) {
    return false;
  }

  // Admins can open all other deals
  if (permissions.has(PermissionEnum.SuperUser)) {
    return true;
  }

  const userCanBuildDeal = permissions.has(PermissionEnum.BuildDeal);
  const userCanClaimDealAsAdvisor = permissions.has(PermissionEnum.ClaimDealAsAdvisor);
  const userIsFinancialSpecialist =
    permissions.has(PermissionEnum.Closer) || permissions.has(PermissionEnum.FinancialSpecialist);

  // We check for keys in the deal because of the type union
  const isClaimedByFinancialSpecialist =
    ('closer_id' in deal && !!deal.closer_id) || ('closer' in deal && !!deal.closer?.id);
  const isClaimedByStructuringManager =
    ('structuring_manager_id' in deal && !!deal.structuring_manager_id) ||
    ('structuring_manager' in deal && !!deal.structuring_manager?.id);
  const isClaimedByAdvisor =
    ('setter_id' in deal && !!deal.setter_id) || ('setter' in deal && !!deal.setter?.id);

  // State specific logic
  switch (deal.state) {
    case DealStateEnum.Estimate:
    case DealStateEnum.SoftClose:
      // Those who have `ClaimDealAsAdvisor` permission can see unclaimed web deals, but should only be able to open them if they are claimed
      return userCanClaimDealAsAdvisor ? isClaimedByAdvisor : true;
    case DealStateEnum.Structuring:
      return userCanBuildDeal && isClaimedByStructuringManager;
    case DealStateEnum.Closing:
    case DealStateEnum.Closed:
      // FSs cannot click into unclaimed web deals
      return !(
        deal.source === DealSource.Web &&
        userIsFinancialSpecialist &&
        !isClaimedByFinancialSpecialist
      );
    default:
      return true;
  }
};

export const setInsertedIds = (
  values: CreditAppOnDeal,
  isCobuyer: boolean,
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void,
  data: NonNullable<
    NonNullable<UpdateCreditAppOnDealMutationResult['data']>['updateCreditAppOnDeal']
  >,
) => {
  if (!values?.customer?.proof_of_insurance?.id && data?.customer?.proof_of_insurance?.id) {
    setFieldValue('customer.proof_of_insurance.id', data.customer?.proof_of_insurance?.id);
  }

  if (!values?.financial_info?.id && data.financial_info?.id) {
    setFieldValue('financial_info.id', data.financial_info.id);
  }

  const personKey = isCobuyer ? 'cobuyer' : 'customer';
  const personValues = values?.[personKey];
  const personRes = data[personKey];

  if (personValues?.id !== personRes?.id) {
    setFieldValue(`${personKey}.id`, personRes?.id);
  }
  if (personValues?.address?.id !== personRes?.address?.id) {
    setFieldValue(`${personKey}.address.id`, personRes?.address?.id);
  }
  if (personValues?.prev_address?.id !== personRes?.prev_address?.id) {
    setFieldValue(`${personKey}.prev_address.id`, personRes?.prev_address?.id);
  }
  if (personValues?.employment?.id !== personRes?.employment?.id) {
    setFieldValue(`${personKey}.employment.id`, personRes?.employment?.id);
  }
  if (personValues?.prev_employment?.id !== personRes?.prev_employment?.id) {
    setFieldValue(`${personKey}.prev_employment.id`, personRes?.prev_employment?.id);
  }
};

export const getLtvRatio = (loanValue: number, bookValue?: number): number =>
  bookValue ? new Big(loanValue).div(bookValue).round(2).toNumber() : 0;

type Field = 'oldPayment';
type FieldDealTypeLabel = {
  [K in Extract<DealType, DealType.Buyout>]: string;
} & Partial<{
  [K in Exclude<DealType, DealType.Buyout>]: string;
}>;

const fieldLabelMap: Record<Field, FieldDealTypeLabel> = {
  oldPayment: {
    marketplace: 'Old Lease Payment',
    buyout: 'Old Lease Payment',
    refi: 'Old Payment',
  },
};

export const getFieldLabel = (field: Field, dealType?: DealType): string =>
  dealType
    ? fieldLabelMap[field][dealType] || fieldLabelMap[field].buyout
    : fieldLabelMap[field].buyout;

type IsDealLockedProps = {
  deal: Pick<Deal, 'lock_status' | 'customer'> | Pick<GeneratedDeal, 'lock_status' | 'customer'>;
  secureLinkFlag: boolean;
};

// Checks if a deal is locked by lock_status or customer entry
export const isDealLocked = ({ deal, secureLinkFlag }: IsDealLockedProps) =>
  deal.lock_status != null || getIsSecureLinkActive(secureLinkFlag, deal.customer);
